/**
 * タイル回転アシストユーティリティ
 *
 * Request 7: Ph.4 タイル配置アシストの追加
 * - 隣接ヘクスの UnfinishedSection や City に繋がる角度に自動回転
 */

import type { Hex, HexCoordinate, TileType, Direction, UnfinishedSection } from '../types';
import { getAdjacentHex, coordsEqual, getOppositeDirection } from '../types';
import { TILE_DEFINITIONS } from '../game/constants/tiles';

/**
 * タイルの接続エッジを回転させた結果を取得
 *
 * @param connections - タイルの接続情報（回転=0の状態）
 * @param rotation - 回転量（0-5、時計回り）
 * @returns 回転後の接続エッジ番号の配列
 */
function getRotatedConnections(connections: number[][], rotation: number): number[][] {
  return connections.map(pair =>
    pair.map(edge => ((edge + rotation) % 6))
  );
}

/**
 * 回転後のタイルが持つ全接続エッジを取得
 *
 * @param tileType - タイルタイプ
 * @param rotation - 回転量（0-5）
 * @returns 接続エッジの番号配列（重複なし）
 */
function getRotatedEdges(tileType: TileType, rotation: number): number[] {
  const tileDef = TILE_DEFINITIONS[tileType];
  if (!tileDef) return [];

  const rotatedConnections = getRotatedConnections(tileDef.connections, rotation);
  const edges = new Set<number>();
  for (const pair of rotatedConnections) {
    for (const edge of pair) {
      edges.add(edge);
    }
  }
  return Array.from(edges);
}

/**
 * 隣接ヘクスに接続すべきエッジを取得
 *
 * @param hexes - 全ヘクスマップ
 * @param coord - 配置先の座標
 * @param unfinishedSections - 未完成セクション
 * @returns 接続すべきエッジ番号の配列（優先度順：UnfinishedSection > City > Town > 既存線路）
 */
function getTargetEdges(
  hexes: Hex[],
  coord: HexCoordinate,
  unfinishedSections: UnfinishedSection[]
): { edges: number[]; priority: number }[] {
  const results: { edge: number; priority: number }[] = [];

  for (let dir = 0; dir < 6; dir++) {
    const neighborCoord = getAdjacentHex(coord, dir as Direction);
    const neighborHex = hexes.find(h =>
      h.coordinate.q === neighborCoord.q && h.coordinate.r === neighborCoord.r
    );

    if (!neighborHex) continue;

    const oppositeDir = getOppositeDirection(dir as Direction);

    // 優先度1: 隣接ヘクスから伸びている UnfinishedSection
    const hasUnfinishedSection = unfinishedSections.some(us =>
      coordsEqual(us.coord, neighborCoord) &&
      (us.exitEdge === oppositeDir || us.entryEdge === oppositeDir)
    );
    if (hasUnfinishedSection) {
      results.push({ edge: dir, priority: 1 });
      continue;
    }

    // 優先度2: 隣接ヘクスが City（全方向接続可能）
    if (neighborHex.city) {
      results.push({ edge: dir, priority: 2 });
      continue;
    }

    // 優先度3: 隣接ヘクスに NewCity タイルが配置済み
    if (neighborHex.tile?.type?.startsWith('NEW_CITY_')) {
      results.push({ edge: dir, priority: 3 });
      continue;
    }

    // 優先度4: 隣接ヘクスに既存線路タイルがあり、oppositeDir に接続エッジがある
    if (neighborHex.tile) {
      const neighborEdges = getRotatedEdges(
        neighborHex.tile.type,
        neighborHex.tile.rotation
      );
      if (neighborEdges.includes(oppositeDir)) {
        results.push({ edge: dir, priority: 4 });
      }
    }
  }

  // 優先度でグループ化
  const grouped: Map<number, number[]> = new Map();
  for (const { edge, priority } of results) {
    if (!grouped.has(priority)) {
      grouped.set(priority, []);
    }
    grouped.get(priority)!.push(edge);
  }

  // 優先度順にソート
  const sortedPriorities = Array.from(grouped.keys()).sort((a, b) => a - b);
  return sortedPriorities.map(p => ({
    edges: grouped.get(p)!,
    priority: p
  }));
}

/**
 * タイルを配置する際の最適な回転を計算
 *
 * @param tileType - 配置するタイルのタイプ
 * @param hexes - 全ヘクスマップ
 * @param coord - 配置先の座標
 * @param unfinishedSections - 未完成セクション
 * @returns 最適な回転（0-5）
 */
export function calculateOptimalRotation(
  tileType: TileType,
  hexes: Hex[],
  coord: HexCoordinate,
  unfinishedSections: UnfinishedSection[]
): 0 | 1 | 2 | 3 | 4 | 5 {
  const tileDef = TILE_DEFINITIONS[tileType];
  if (!tileDef) return 0;

  // NewCityタイルは回転不要（全方向接続可能）
  if (tileDef.isNewCity) return 0;

  const targetEdgeGroups = getTargetEdges(hexes, coord, unfinishedSections);

  // 接続先がない場合はデフォルト回転
  if (targetEdgeGroups.length === 0) return 0;

  // 最も優先度の高いエッジグループ
  const highestPriorityEdges = targetEdgeGroups[0].edges;

  // 各回転について、最優先エッジに何個接続できるかをスコア化
  let bestRotation = 0;
  let bestScore = 0;

  for (let rotation = 0; rotation < 6; rotation++) {
    const rotatedEdges = getRotatedEdges(tileType, rotation);

    // 最優先エッジへの接続数
    let score = 0;
    for (const targetEdge of highestPriorityEdges) {
      if (rotatedEdges.includes(targetEdge)) {
        score += 10; // 最優先エッジへの接続は高スコア
      }
    }

    // 2番目以降の優先度のエッジへの接続も加点
    for (let i = 1; i < targetEdgeGroups.length; i++) {
      for (const targetEdge of targetEdgeGroups[i].edges) {
        if (rotatedEdges.includes(targetEdge)) {
          score += (5 - i); // 優先度が低いほど加点が少ない
        }
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestRotation = rotation;
    }
  }

  return bestRotation as 0 | 1 | 2 | 3 | 4 | 5;
}
